import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Table, Pagination, TableData, TableDataTypes } from 'src/app/shared/types';
import { GuestDetailsService } from '../guest-details.service';
import { Router } from '@angular/router';
import * as moment from 'moment';

@Component({
  selector: 'app-guest-wallet-transactions',
  templateUrl: './guest-wallet-transactions.component.html',
  styleUrls: ['./guest-wallet-transactions.component.css']
})
export class GuestWalletTransactionsComponent implements OnInit, OnChanges {
  @Input() profileId: string;
  @Input() guestDetailsToggleableHeaders: string[];
  @Output() toggleHeaders: EventEmitter<any> = new EventEmitter();
  transactionsTable: Table;
  transactions: any[] = [];
  pageNumber: number = 0;
  offset: number = 0;
  perPage: number = 10;
  dataLoaded: boolean = false;

  constructor(
    private guestDetailsService: GuestDetailsService,
    private router: Router
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileId && changes.profileId.currentValue) {
      this.profileId = changes.profileId.currentValue;
      if (this.profileId) {
        this.initializeTable();
        this.fillDataToTable();
      }
    }
  }

  ngOnInit() {
    if (this.profileId) {
      this.initializeTable();
      this.fillDataToTable();
    }
  }

  changeInHeader(header) {
    this.toggleHeaders.emit(header);
  }

  initializeTable(pageNumber = 0) {
    this.transactionsTable = new Table({
      toggleableHeaders: this.guestDetailsToggleableHeaders,
      selectedHeader: this.guestDetailsToggleableHeaders.indexOf('Wallet Transactions'),
      headers: {
        dateTime: 'Date & Time',
        transactionType: 'Transaction Type',
        description: 'Title',
        orderId: 'Order ID',
        amount: 'Amount',
        status: 'Status'
      },
      pagination: new Pagination(pageNumber, this.perPage),
      defaultError: 'No transactions found',
      data: [],
      config: {
        refresh: true,
      },
      onRefresh: () => {
        this.transactionsTable = null;
        this.ngOnInit();
      }
    });
  }

  onPaginationChange(event) {
    this.pageNumber = event.pageNo;
    this.offset = event.pageNo * event.perPage;
    this.perPage = event.perPage;
    this.fillDataToTable();
  }

  fillDataToTable() {
    if (this.profileId) {
      this.dataLoaded = false;
      this.guestDetailsService.getGuestWalletTransactions(this.profileId, this.perPage, this.offset).subscribe({
        next: (response: any) => {
          this.dataLoaded = true;
          const transactionsData = response && response.data ? response.data : response;
          const transactionsList = transactionsData.transactions || [];
          const totalTransactions = transactionsData.totalTransactions || 0;

          const self = this;
          function createTableData(value, className?, onClick?) {
            return new TableData({
              data: value,
              type: TableDataTypes.DATA,
              className: className ? className + ' cursor-pointer' : 'cursor-pointer whiteSpacePre',
              onClick: onClick
            });
          }

          this.transactions = transactionsList.map((transaction) => {
            const orderId = transaction.orderId;
            const goToOrderPage = () => {
              if (orderId && !orderId.startsWith('redeem_') && !orderId.startsWith('credit_')) {
                this.router.navigate([`/order/${orderId}`]);
              }
            };

            const description = this.mapTransactionDescription(transaction.title, transaction.reason);
            const transactionTypeDisplay = transaction.transactionType ? transaction.transactionType.charAt(0).toUpperCase() + transaction.transactionType.slice(1) : 'N/A';
            const formattedDate = this.formatDateToIST(transaction.transactionDate);
            const formattedAmount = this.formatAmount(transaction.amount, transaction.transactionType);
            const statusDisplay = this.mapStatus(transaction.transactionStatus);

            return {
              dateTime: createTableData(formattedDate),
              transactionType: createTableData(transactionTypeDisplay),
              description: createTableData(description),
              orderId: createTableData(orderId, null, goToOrderPage),
              amount: createTableData(formattedAmount, transaction.transactionType === 'debit' ? 'color-red' : 'color-green'),
              status: createTableData(statusDisplay)
            };
          });

          this.transactionsTable.data = this.transactions;
          this.transactionsTable.pagination.count = totalTransactions;
        },
        error: (err) => {
          this.dataLoaded = true;
          this.transactionsTable.data = [];
          this.transactionsTable.pagination.count = 0;
        }
      });
    } else {
      this.dataLoaded = true;
    }
  }

  mapTransactionDescription(title: string, reason: string): string {
    if (title === 'pool_redeem') {
      return 'Redeem';
    }
    if (title === 'pool_payment') {
      return 'Order Payment';
    }
    if (title === 'pool_referral') {
      return 'Referral';
    }

    // Fallback: format title
    if (title) {
      return title.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'N/A';
  }

  mapStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'done': 'Completed',
      'pending': 'Pending',
      'failed': 'Failed',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A');
  }

  formatDateToIST(dateString: string): string {
    if (!dateString) {
      return 'N/A';
    }
    // Convert UTC to IST (UTC+5:30)
    const istDate = moment.utc(dateString).utcOffset('+05:30');
    const dateInfo = istDate.format('DD MMM YYYY');
    const timeInfo = istDate.format('HH:mm:ss');
    return `${dateInfo}\n${timeInfo}`;
  }

  formatAmount(amount: number, transactionType: string): string {
    if (amount === null || amount === undefined) {
      return 'N/A';
    }
    const formattedAmount = Math.abs(amount).toFixed(2);
    const sign = transactionType === 'debit' ? '-' : '';
    return `${sign}₹${formattedAmount}`;
  }
}

